import type { ThemeConfig } from "antd";

import type { TableDensity } from "@/contexts/TableDensityContext";

export const theme = {
  token: {
    colorPrimary: "#0C98D0",
    colorBgContainer: "#ffffff",
    fontFamily: "Work Sans",
    // Destructive palette. Without these antd falls back to its own red
    // (#ff4d4f), which is off-brand next to the #EF4444 used throughout.
    colorError: "#EF4444",
    colorErrorHover: "#F87171",
    colorErrorActive: "#DC2626",
    colorErrorBg: "#FEE2E2",
    colorErrorBorder: "#FCA5A5",
  },
  components: {
    Layout: {
      bodyBg: "#EFF1F2",
      footerBg: "#EFF1F2",
      headerBg: "transparent",
      siderBg: "linear-gradient(180deg, #17242c 0%, #094b68 100%)",
    },
    Segmented: {
      itemActiveBg: "#ffffff",
      itemSelectedBg: "#0000000f",
      itemSelectedColor: "#2B4752",
      itemHoverBg: "#ffffff",
      trackBg: "#ffffff",
    },
    Menu: {
      darkItemColor: "rgba(255,255,255,0.65)",
      darkItemHoverColor: "#fff",
      darkItemSelectedColor: "#fff",
      darkItemSelectedBg: "#ffffff1a",
      darkSubMenuItemBg: "transparent",
      darkItemBg: "transparent",
      darkPopupBg: "rgba(18, 52, 68, 0.95)",
      // colorLink: "rgba(255,255,255,0.65)",

      itemHeight: "40px",
    },
    Button: {
      borderRadius: 4,
    },
    Input: {
      borderRadius: 4,
    },
    Select: {
      borderRadius: 4,
    },
    Collapse: {
      borderRadius: 4,
    },
  },
};

/**
 * Block (vertical) cell padding per antd table size when compact mode is on.
 *
 * antd builds a row's height from `fontSize * lineHeight` plus twice the block
 * padding for that size, so cutting the padding is what actually shortens the
 * row. Inline padding is deliberately left alone: the complaint is about
 * vertical whitespace, and narrowing the gutters as well makes columns collide.
 *
 * antd's defaults are 16 / 12 / 8, giving ~55 / 47 / 39px rows; these give
 * ~39 / 35 / 29px. Most Vivo tables render at `size="small"`, so the SM value
 * is the one that matters in practice — measured at 39px -> 29px in a browser.
 */
const COMPACT_TABLE_TOKENS = {
  cellPaddingBlock: 8,
  cellPaddingBlockMD: 6,
  cellPaddingBlockSM: 3,
};

/**
 * The antd theme for a given table density. `default` returns the base theme
 * untouched, so nothing changes for users who never turn compact mode on.
 */
export function themeForDensity(density: TableDensity): ThemeConfig {
  if (density !== "compact") return theme;

  return {
    ...theme,
    components: {
      ...theme.components,
      Table: COMPACT_TABLE_TOKENS,
    },
  };
}
